<template>
    <section class="flex flex-col gap-5">
        <div v-if="loading" class="scaffold-body-small text-ink-muted">Loading…</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />

        <template v-else-if="cycle">
            <BaseButton theme="tertiary" size="sm" class="self-start" @click="emit('back')"> <i class="bi bi-chevron-left" aria-hidden="true"></i> Back </BaseButton>

            <div class="flex items-start justify-between gap-4 flex-wrap">
                <div class="flex items-start gap-3 min-w-0">
                    <ReflectionIcon :kind="cycle.is_self_assessment_only ? 'self' : 'sixty'" size="md" class="mt-1" />
                    <div class="min-w-0 flex flex-col gap-1">
                        <h2 class="title-body text-ink leading-tight">{{ headerTitle }}</h2>
                        <p v-if="reportDate" class="scaffold-details-small text-ink-muted">{{ reportDate }}</p>
                    </div>
                </div>
                <BasePopover v-if="showResponders" as-child show-arrow align="end" :side-offset="6" content-class="w-[264px] rounded-xl p-2 shadow-lg">
                    <template #trigger>
                        <BaseButton theme="tertiary" size="sm" class="shrink-0" @click="onViewResponders">
                            <span class="flex -space-x-2">
                                <BaseAvatar
                                    v-for="(avatar, i) in raterAvatars"
                                    :key="`ra-${i}`"
                                    size="2xs"
                                    :initials="avatar.initial"
                                    class="ring-[1.5px] ring-surface-raised"
                                    :style="{ '--base-avatar-bg': avatar.swatch.fill, '--base-avatar-text': avatar.swatch.text }" />
                            </span>
                            <span>View Responders</span>
                        </BaseButton>
                    </template>
                    <template #content>
                        <p class="scaffold-details-bold text-ink-muted px-2 pt-1">Responders</p>
                        <p class="scaffold-details-small text-ink-muted px-2 pb-1">{{ raterCount }} {{ raterCount === 1 ? "response" : "responses" }}</p>
                        <ul class="flex flex-col">
                            <li v-for="entry in responderRows" :key="entry.role" class="flex items-center gap-2 px-2 py-2">
                                <BaseAvatar
                                    size="2xs"
                                    :initials="entry.label.charAt(0).toUpperCase()"
                                    :style="{ '--base-avatar-bg': entry.swatch.fill, '--base-avatar-text': entry.swatch.text }" />
                                <span class="scaffold-body-small text-ink">{{ entry.label }}</span>
                                <span class="scaffold-details-small text-ink-muted ml-auto">{{ entry.count }}</span>
                            </li>
                        </ul>
                    </template>
                </BasePopover>
            </div>

            <p v-if="lede" class="scaffold-body-small text-ink-muted">{{ lede }}</p>

            <MissingSelfReflectionNotice v-if="report" :cycle="cycle" :viewer-is-subject="viewerIsSubject" />

            <div v-if="report && viewerIsSubject" class="flex items-center gap-2 flex-wrap">
                <BaseButton theme="neutral" size="sm" :disabled="downloading" class="inline-flex items-center gap-2" @click="onDownloadPdf">
                    <i class="bi bi-download" aria-hidden="true"></i>
                    {{ downloading ? "Downloading…" : "Download PDF" }}
                </BaseButton>
                <BaseButton theme="primary" size="sm" :disabled="startingChat" class="inline-flex items-center gap-2" @click="onChatWithNadia">
                    <i class="bi bi-stars" aria-hidden="true"></i>
                    {{ startingChat ? "Starting…" : "Chat with Nadia" }}
                </BaseButton>
            </div>

            <AwaitingSelfReflectionGate
                v-if="!report && cycle.awaiting_self_reflection && viewerIsSubject"
                :cycle-id="cycleId"
                :can-generate-without="cycle.can_generate_without_self_reflection ?? false"
                :responded-count="cycle.responded_count ?? 0"
                :total-invited="cycle.total_invited ?? 0"
                @complete="emit('open-self', { cycleId: cycle.id, title: cycle.template.name, closesAt: null })"
                @generated="onGeneratedWithoutSelf" />

            <BaseCard v-else-if="!report" theme="muted" padding="lg" class="border-dashed border-border-strong text-center">
                <div class="flex flex-col items-center gap-3">
                    <LoadingSpinner v-if="waitingOnReport" class="w-5 h-5 text-ink-muted" />
                    <p class="body-small text-ink-muted" role="status" aria-live="polite">{{ reportStatusMessage }}</p>
                    <BaseButton v-if="!waitingOnReport && !cycle.closed_without_report" theme="neutral" size="sm" @click="checkAgain">Check again</BaseButton>
                </div>
            </BaseCard>

            <template v-else>
                <p v-if="cycle.report_intro" class="body-small text-ink whitespace-pre-line">{{ cycle.report_intro }}</p>
                <ExecutiveSummarySection v-if="report.executive_summary" :summary="report.executive_summary" :heading-level="3" />

                <SuggestedGoalsSection
                    v-if="showSuggestedGoals"
                    :cycle-id="cycleId"
                    :development-journeys-enabled="developmentJourneysEnabled"
                    :assessment-goal-discussion-chat-start-url="assessmentGoalDiscussionChatStartUrl"
                    :assessment-goal-adoption-url="assessmentGoalAdoptionUrl"
                    :assessment-goal-dismissal-url="assessmentGoalDismissalUrl"
                    @hidden="suggestedGoalsHidden = true" />

                <!-- LAB-988: self-only cycles get their own bars + histogram.
                     360 cycles keep the existing shared components since they use
                     rater breakdowns, cuts, self-vs-others, and anonymity handling that
                     don't apply to a single self response. -->
                <template v-if="showScores && cycle.is_self_assessment_only">
                    <SelfCategoryScoresSection
                        v-if="aggregates && hasCategoryScores"
                        id="category-scores"
                        :sections="aggregates.sections ?? []"
                        :max-score="defaultMaxScore"
                        :accent-color="ACCENT_SELF" />
                    <SelfQuestionByQuestionSection
                        v-if="aggregates"
                        :questions="questionByQuestionItems"
                        :max-score="defaultMaxScore"
                        :subject-first-name="cycle.subject_first_name" />
                </template>
                <template v-else-if="showScores">
                    <CategoryScoresSection
                        v-if="aggregates && hasCategoryScores"
                        id="category-scores"
                        :sections="aggregates.sections ?? []"
                        :self-assess-enabled="cycle.self_assess_enabled && cycle.self_assessment_submitted"
                        :is-self-assessment-only="cycle.is_self_assessment_only"
                        :subject-name="viewerIsSubject ? '' : subjectName"
                        :max-score="defaultMaxScore" />

                    <SelfVsOthersSection v-if="aggregates && hasSelfVsOthers" :questions="scoreQuestions" :subject-name="viewerIsSubject ? '' : subjectName" />

                    <QuestionByQuestionSection
                        v-if="aggregates"
                        :questions="questionByQuestionItems"
                        :sections="aggregates.sections ?? []"
                        :subject-first-name="cycle.subject_first_name"
                        :is-self-assessment-only="cycle.is_self_assessment_only"
                        :viewer-is-subject="viewerIsSubject"
                        :anonymity-threshold="aggregates.anonymity_threshold" />
                </template>

                <ThemesSection v-if="showScores && (report.themes?.length ?? 0) > 0 && !cycle.is_self_assessment_only" :themes="report.themes" class="mt-2.5" />
            </template>
        </template>
    </section>
</template>

<script setup lang="ts">
import { useTimeoutPoll } from "@vueuse/core";
import { fetchApi, useFetch } from "~vue/api";
import AwaitingSelfReflectionGate from "~vue/AssessmentResults/AwaitingSelfReflectionGate.vue";
import CategoryScoresSection from "~vue/AssessmentResults/CategoryScoresSection.vue";
import ExecutiveSummarySection from "~vue/AssessmentResults/ExecutiveSummarySection.vue";
import MissingSelfReflectionNotice from "~vue/AssessmentResults/MissingSelfReflectionNotice.vue";
import QuestionByQuestionSection from "~vue/AssessmentResults/QuestionByQuestionSection.vue";
import SelfVsOthersSection from "~vue/AssessmentResults/SelfVsOthersSection.vue";
import SuggestedGoalsSection from "~vue/AssessmentResults/SuggestedGoalsSection.vue";
import ThemesSection from "~vue/AssessmentResults/ThemesSection.vue";
import type { Aggregates, CycleView, PerQuestion, Report } from "~vue/AssessmentResults/types";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePopover from "~vue/components/BasePopover.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import ReflectionIcon from "~vue/pages/reflections/ReflectionIcon.vue";
import SelfCategoryScoresSection from "~vue/pages/reflections/SelfCategoryScoresSection.vue";
import SelfQuestionByQuestionSection from "~vue/pages/reflections/SelfQuestionByQuestionSection.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { getIndexedAvatarSwatch } from "~vue/utils/perspectivePalette";
import { CONNECTION_LOAD_MESSAGE, DEFAULT_RATER_RELATIONSHIPS, formatDate, isNetworkError } from "~vue/utils/threeSixty";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    cycleId: string;
    showSuggestedGoals?: boolean;
    developmentJourneysEnabled?: boolean;
    assessmentGoalDiscussionChatStartUrl?: string | null;
    assessmentGoalAdoptionUrl?: string | null;
    assessmentGoalDismissalUrl?: string | null;
}

const {
    cycleId,
    showSuggestedGoals: showSuggestedGoalsProp = false,
    developmentJourneysEnabled = false,
    assessmentGoalDiscussionChatStartUrl = null,
    assessmentGoalAdoptionUrl = null,
    assessmentGoalDismissalUrl = null,
} = defineProps<Props>();

const emit = defineEmits<{
    (event: "back"): void;
    (event: "open-self", payload: { cycleId: string; title: string; closesAt: string | null }): void;
}>();

// Only the self-only branch below renders the Self* sections, so this is the
// self-reflection amber — the same accent the taker uses for its score tiles.
// Orange is the ledger's self-reflect family and its pending status, so both
// ride the same status token on purpose.
const ACCENT_SELF = "var(--color-status-warning)";

const cycle = ref<CycleView | null>(null);
const report = ref<Report | null>(null);
const viewerIsSubject = ref(true);
const subjectName = computed(() => cycle.value?.subject_first_name ?? "");
const loading = ref(true);
const loadError = ref<string | null>(null);
const suggestedGoalsHidden = ref(false);
const showSuggestedGoals = computed(() => showSuggestedGoalsProp && !suggestedGoalsHidden.value && viewerIsSubject.value);

const aggregates = computed<Aggregates | null>(() => report.value?.aggregates ?? null);
// Template-wide superset of the per-section rule in SelfCategoryScoresSection /
// CategoryScoresSection: the report ends after the suggested goals.
const showScores = computed(() => !cycle.value?.hide_report_scores);
const scoreQuestions = computed<PerQuestion[]>(() => aggregates.value?.per_question.filter((q) => q.is_score) ?? []);
const questionByQuestionItems = computed<PerQuestion[]>(() => aggregates.value?.per_question.filter((q) => q.is_score || q.open_text_synthesis) ?? []);
const hasSelfVsOthers = computed(() => scoreQuestions.value.some((q) => q.self_score !== null && q.all_avg !== null));
const hasCategoryScores = computed(() => (aggregates.value?.sections ?? []).some((s) => s.avg !== null));

const headerTitle = computed(() => {
    if (!cycle.value) {
        return "";
    }
    if (cycle.value.is_self_assessment_only) {
        return `${cycle.value.template.name} Results`;
    }
    return viewerIsSubject.value ? "360 Report" : `${subjectName.value}'s 360 Report`;
});

const lede = computed(() => {
    if (!cycle.value) {
        return "";
    }
    if (cycle.value.is_self_assessment_only) {
        return "Gain insights into how best to approach your work and adapt as ways of working evolve.";
    }
    return "";
});

const reportDate = computed(() => formatDate(report.value?.generated_at ?? cycle.value?.unlocked_at ?? null, "long"));

const RATER_AVATAR_MAX = 6;
const raterCount = computed(() => Object.values(aggregates.value?.rater_breakdown ?? {}).reduce((a, b) => a + b, 0));
const raterAvatars = computed(() =>
    Array.from({ length: Math.min(raterCount.value, RATER_AVATAR_MAX) }, (_v, i) => ({ initial: String.fromCharCode(65 + i), swatch: getIndexedAvatarSwatch(i) })),
);

// Rater identities are never in the report payload, so the popover lists the
// relationship mix instead of names.
const builtinRelationshipLabels = Object.fromEntries(DEFAULT_RATER_RELATIONSHIPS.map((option) => [option.value, option.label]));
const relationshipLabels = computed(() => aggregates.value?.relationship_labels ?? builtinRelationshipLabels);
const responderRows = computed(() =>
    Object.entries(aggregates.value?.rater_breakdown ?? {}).map(([role, count], i) => ({
        role,
        label: relationshipLabels.value[role] ?? role,
        count,
        swatch: getIndexedAvatarSwatch(i),
    })),
);

// The relationship mix is still rater status: a subject who invited one manager
// reads "manager: 1" as that manager having responded. An organization that
// anonymizes rater status drops the responder cluster entirely.
const showResponders = computed(() => cycle.value !== null && !cycle.value.is_self_assessment_only && !cycle.value.rater_status_hidden && raterAvatars.value.length > 0);

function onViewResponders() {
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RESULTS_VIEWED, {
        cycle_id: cycleId,
        action: "view_responders_toggle",
    });
}

const defaultMaxScore = computed(() => {
    const first = scoreQuestions.value[0];
    return first?.max_score ?? first?.distribution.length ?? 5;
});

const startingChat = ref(false);
const downloading = ref(false);
const { t } = useI18n();

// What the panel is doing while there is no report yet. Derived from the poll's
// own outcomes rather than from `isActive`, which can't tell "gave up" from
// "the request failed" — and both used to read as "still generating".
type ReportStatus = "polling" | "slow" | "timed_out" | "error";

const reportStatus = ref<ReportStatus>("polling");
const reportError = ref<string | null>(null);

// Report generation is a queued Celery task that makes three sequential LLM
// calls, so a wait of a minute or two is ordinary and a backed-up worker is a
// normal production state. Poll tightly while a fast report is plausible, then
// back off so a long wait costs a handful of requests instead of a hundred.
// The ceiling matches what the CSV import dialogs allow for queued Celery work.
const POLL_INTERVAL_MS = 3_000;
const POLL_SLOW_INTERVAL_MS = 10_000;
const POLL_SLOW_AFTER_MS = 30_000;
const POLL_CEILING_MS = 5 * 60 * 1_000;

let pollStartedAt = 0;

// A cycle that closed for good with no report has nothing on its way.
const closedWithoutReport = computed(() => Boolean(cycle.value?.closed_without_report));
const waitingOnReport = computed(() => !closedWithoutReport.value && (reportStatus.value === "polling" || reportStatus.value === "slow"));

const UNAVAILABLE_MESSAGE = "We couldn't check on your report just now.";
const STATUS_MESSAGES: Record<Exclude<ReportStatus, "error">, string> = {
    polling: "Generating your report…",
    slow: "This is taking longer than usual. Still working on your report…",
    timed_out: "Your report still isn't ready. We've stopped checking for now.",
};

const reportStatusMessage = computed(() => {
    if (closedWithoutReport.value) {
        return t("reflections.results.closedWithoutReport");
    }
    return reportStatus.value === "error" ? (reportError.value ?? UNAVAILABLE_MESSAGE) : STATUS_MESSAGES[reportStatus.value];
});

async function pollForReport() {
    const elapsedMs = Date.now() - pollStartedAt;
    if (elapsedMs > POLL_CEILING_MS) {
        reportStatus.value = "timed_out";
        stopPolling();
        return;
    }
    const { data, error: fetchError } = await useFetch(`/v1/reflections/cycles/${cycleId}/results`).json();
    if (fetchError.value) {
        // A 500 from the API and "not ready yet" used to look identical,
        // because a failed request just leaves `data` null. A dropped
        // connection is named as one, the same as on every other panel —
        // otherwise the poll reports a browser string like "Failed to fetch".
        reportError.value = isNetworkError(fetchError.value)
            ? CONNECTION_LOAD_MESSAGE
            : (data.value as { error?: string } | null)?.error || fetchError.value.message || UNAVAILABLE_MESSAGE;
        reportStatus.value = "error";
        stopPolling();
        return;
    }
    const payload = data.value as { cycle: CycleView; report: Report | null; viewer_is_subject?: boolean } | null;
    if (payload?.report) {
        cycle.value = payload.cycle;
        report.value = payload.report;
        viewerIsSubject.value = payload.viewer_is_subject ?? true;
        stopPolling();
        return;
    }
    if (elapsedMs > POLL_SLOW_AFTER_MS) {
        reportStatus.value = "slow";
    }
}

const { resume: resumePoll, pause: stopPolling } = useTimeoutPoll(
    pollForReport,
    // The backoff lives here: `useTimeoutPoll` re-reads the interval after each
    // tick, so no separate timer is introduced (and `pause()` clears the only one).
    () => (reportStatus.value === "slow" ? POLL_SLOW_INTERVAL_MS : POLL_INTERVAL_MS),
    { immediate: false },
);

// Stop the poll on unmount so we don't keep hitting /results in the background
// after the user navigates back to the hub list. This also settles the backoff
// timer: `pause()` stops the loop rescheduling, and `useTimeoutFn` clears the
// pending timeout itself on scope dispose.
onUnmounted(() => stopPolling());

// The report is queued from the raters alone; drop the gate and poll for it
// the same way a freshly unlocked cycle does.
function onGeneratedWithoutSelf() {
    if (cycle.value) {
        cycle.value = { ...cycle.value, awaiting_self_reflection: false, can_generate_without_self_reflection: false };
    }
    checkAgain();
}

/** Restart the poll from zero. The page owns the refresh, so nothing here
 * should ever ask the user to reload. */
function checkAgain() {
    pollStartedAt = Date.now();
    reportStatus.value = "polling";
    reportError.value = null;
    // Arm the loop first, then check straight away: `resume()` alone would sit
    // idle for a full interval, so the button would look like it did nothing.
    resumePoll();
    void pollForReport();
}

async function onDownloadPdf() {
    if (downloading.value) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RESULTS_PDF_DOWNLOAD_CLICKED, { cycle_id: cycleId });
    downloading.value = true;
    try {
        const response = await fetchApi(`/v1/reflections/cycles/${cycleId}/export-pdf`);
        if (!response.ok) {
            return;
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const disposition = response.headers.get("Content-Disposition");
        const match = disposition?.match(/filename="?([^"]+)"?/);
        a.download = match?.[1] ?? (cycle.value?.is_self_assessment_only ? "self_assessment_report.pdf" : "360_report.pdf");
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    } finally {
        downloading.value = false;
    }
}

async function onChatWithNadia() {
    if (startingChat.value) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RESULTS_CHAT_WITH_NADIA_CLICKED, { cycle_id: cycleId });
    startingChat.value = true;
    try {
        const { data } = await useFetch(`/v1/reflections/cycles/${cycleId}/debrief-chat`).post().json();
        const chatUrl = (data.value as { chat_url?: string })?.chat_url;
        if (chatUrl) {
            globalThis.location.href = chatUrl;
        }
    } finally {
        startingChat.value = false;
    }
}

onMounted(async () => {
    const { data, error: fetchError, response } = await useFetch(`/v1/reflections/cycles/${cycleId}/results`).json();
    loading.value = false;
    if (fetchError.value) {
        if (response.value?.status === 400) {
            // Cycle not yet unlocked — bounce back to the hub list.
            emit("back");
            return;
        }
        loadError.value = (data.value as { error?: string })?.error || "Could not load results.";
        return;
    }
    const payload = data.value as { cycle: CycleView; report: Report | null; viewer_is_subject?: boolean };
    cycle.value = payload.cycle;
    report.value = payload.report;
    viewerIsSubject.value = payload.viewer_is_subject ?? true;
    const ratersByRole = payload.report?.aggregates?.rater_breakdown ?? {};
    const raterCount = Object.values(ratersByRole).reduce((a, b) => a + b, 0);
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RESULTS_VIEWED, {
        cycle_id: cycleId,
        report_ready: payload.report !== null,
        rater_count: raterCount,
        has_self_assess: payload.cycle.self_assess_enabled,
    });

    // An awaiting cycle has no report on the way until the subject acts, and
    // a closed reportless one never will, so there is nothing to poll for.
    if (!payload.report && !payload.cycle.awaiting_self_reflection && !payload.cycle.closed_without_report) {
        pollStartedAt = Date.now();
        resumePoll();
    }
});
</script>
